import React from 'react'
import { Bar } from '@nivo/bar'
import { Line } from '@nivo/line'
import { Pie } from '@nivo/pie'
import { ChartSpec } from './chartSpec'
import { resolveColor, formatValue, nivoTheme } from './chartPalette'

const MARGIN = { top: 16, right: 24, bottom: 56, left: 64 }

// Props the LLM may NOT override via the `nivo` passthrough (we own data + size).
const PROTECTED = new Set(['width', 'height', 'data'])

/** Merge the LLM's raw Nivo props over our defaults so it can drive the library directly
 *  (colours, axes, margins, curve, legends, …) without being able to break data/size. */
const withNivo = (
  base: Record<string, unknown>,
  extra: Record<string, unknown> | undefined
): Record<string, unknown> => {
  if (!extra) return base
  const out = { ...base }
  for (const k of Object.keys(extra)) if (!PROTECTED.has(k)) out[k] = extra[k]
  return out
}

/** Render a validated ChartSpec with Nivo at an explicit size. Fixed-size components (not
 *  Responsive*) so charts render under React StrictMode. The LLM controls presentation via
 *  spec fields and the `nivo` passthrough; this just supplies defaults. */
const ChartRenderer: React.FC<{
  spec: ChartSpec
  width: number
  height: number
}> = ({ spec, width, height }) => {
  const series = spec.series ?? []
  const xKey = spec.x ?? 'label'
  const compact = spec.valueFormat === 'compact'
  const fmt = (v: number | string | null | undefined) =>
    formatValue(v, spec.format, compact)
  const xRotation = spec.xLabelRotation ?? 0
  const tickValues = spec.maxTicks // undefined => Nivo auto

  // Distinct colour per series (auto-assigned from the categorical palette unless named).
  const colorByKey: Record<string, string> = {}
  series.forEach((s, i) => {
    colorByKey[s.name ?? s.key] = resolveColor(s.color, i)
  })

  if (spec.type === 'bar') {
    const keys = series.map(s => s.name ?? s.key)
    const data = spec.data.map(row => {
      const out: Record<string, unknown> = { [xKey]: row[xKey] }
      series.forEach(s => {
        out[s.name ?? s.key] = row[s.key]
      })
      return out
    })
    const horizontal = spec.orientation === 'horizontal'
    const valueAxis = { format: (v: number | string) => fmt(v), tickValues }
    const longestLabel = data.reduce(
      (m, r) => Math.max(m, String(r[xKey] ?? '').length),
      0
    )
    const props = withNivo(
      {
        keys,
        indexBy: xKey,
        layout: horizontal ? 'horizontal' : 'vertical',
        groupMode: spec.stacked ? 'stacked' : 'grouped',
        margin: {
          top: keys.length > 1 ? 28 : 16,
          right: 24,
          bottom: horizontal ? 56 : xRotation ? 92 : 56,
          left: horizontal ? Math.min(200, longestLabel * 7 + 24) : 64,
        },
        padding: 0.3,
        theme: nivoTheme,
        colors: (bar: { id: string | number }) => colorByKey[bar.id as string],
        borderRadius: 3,
        axisBottom: horizontal ? valueAxis : { tickRotation: xRotation },
        axisLeft: horizontal ? {} : valueAxis,
        enableLabel: false,
        valueFormat: (v: number) => fmt(v),
        legends:
          keys.length > 1
            ? [
                {
                  dataFrom: 'keys',
                  anchor: 'top-right',
                  direction: 'row',
                  translateY: -8,
                  itemWidth: 110,
                  itemHeight: 16,
                  symbolSize: 12,
                },
              ]
            : [],
        animate: true,
      },
      spec.nivo
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <Bar width={width} height={height} data={data as any} {...(props as any)} />
  }

  if (spec.type === 'line' || spec.type === 'area') {
    const lineData = series.map(s => ({
      id: s.name ?? s.key,
      data: spec.data.map(row => ({
        x: row[xKey] as string,
        y: row[s.key] as number,
      })),
    }))
    const props = withNivo(
      {
        margin: { ...MARGIN, bottom: xRotation ? 84 : MARGIN.bottom },
        theme: nivoTheme,
        colors: (line: { id: string }) => colorByKey[line.id],
        xScale: { type: 'point' },
        yScale: { type: 'linear', min: 'auto', max: 'auto' },
        axisLeft: { format: (v: number) => fmt(v), tickValues },
        axisBottom: { tickRotation: xRotation },
        enableArea: spec.type === 'area',
        areaOpacity: 0.12,
        enablePoints: true,
        pointSize: 7,
        useMesh: true,
        yFormat: (v: number) => fmt(v),
        curve: 'monotoneX',
        legends:
          series.length > 1
            ? [
                {
                  anchor: 'top-right',
                  direction: 'row',
                  translateY: -8,
                  itemWidth: 110,
                  itemHeight: 16,
                  symbolSize: 12,
                },
              ]
            : [],
        animate: true,
      },
      spec.nivo
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <Line width={width} height={height} data={lineData as any} {...(props as any)} />
  }

  // pie / donut
  const valueKey = series[0]?.key
  const pieData = spec.data.map((row, i) => ({
    id: String(row[xKey] ?? i),
    label: String(row[xKey] ?? i),
    value: Number(row[valueKey]),
  }))
  const pieColors = pieData.map((_, i) => resolveColor(undefined, i))
  const pieProps = withNivo(
    {
      margin: { top: 16, right: 16, bottom: 24, left: 16 },
      theme: nivoTheme,
      colors: pieColors,
      innerRadius: spec.type === 'donut' ? 0.6 : 0,
      padAngle: 0.6,
      cornerRadius: 3,
      borderWidth: 0,
      arcLinkLabelsColor: { from: 'color' },
      arcLinkLabelsThickness: 2,
      valueFormat: (v: number) => fmt(v),
      activeOuterRadiusOffset: 6,
      animate: true,
    },
    spec.nivo
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <Pie width={width} height={height} data={pieData as any} {...(pieProps as any)} />
}

export default ChartRenderer
